"use client";

import { useEffect, useRef } from "react";
import { ChunkQueueManager } from "@/shared/lib/chunk-queue-manager";
import type { ChunkMessage } from "../types/websocket-messages";

/**
 * Chunk Queue 관리 훅
 *
 * 책임:
 * - Queue Manager 생명주기 관리
 * - 턴 변경 감지 및 큐 초기화
 * - 청크 소비자 등록
 */

// 싱글톤 Queue Manager
let globalVoiceChunkQueue: ChunkQueueManager<ChunkMessage> | null = null;

/**
 * Voice Chunk Queue 접근
 */
export function getVoiceChunkQueue(): ChunkQueueManager<ChunkMessage> {
  if (!globalVoiceChunkQueue) {
    globalVoiceChunkQueue = new ChunkQueueManager<ChunkMessage>({
      maxQueueSize: 100,
      logEnabled: false,
    });
  }
  return globalVoiceChunkQueue;
}

/**
 * Voice Chunk Queue 사용 훅
 */
export function useVoiceChunkQueue() {
  const queueRef = useRef<ChunkQueueManager<ChunkMessage>>(getVoiceChunkQueue());

  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시에는 정리하지 않음 (싱글톤)
    };
  }, []);

  return {
    queue: queueRef.current,
    enqueue: (chunk: ChunkMessage) => queueRef.current.enqueue(chunk),
    subscribe: (consumer: (chunk: ChunkMessage | "END") => void) =>
      queueRef.current.subscribe(consumer),
    startNewTurn: (turnId: string) => queueRef.current.startNewTurn(turnId),
    clear: () => queueRef.current.clear(),
    getState: () => queueRef.current.getState(),
  };
}

/**
 * 텍스트 청크는 별도 관리 (순서 보장 필요 없음)
 */
export function useTextChunkQueue() {
  // 텍스트는 실시간 스트리밍이므로 큐가 아닌 직접 처리
  // 필요시 나중에 구현
  return null;
}

/**
 * Queue Manager 초기화 (테스트용)
 */
export function resetVoiceChunkQueue() {
  if (globalVoiceChunkQueue) {
    globalVoiceChunkQueue.destroy();
    globalVoiceChunkQueue = null;
  }
}
